import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const CookieBanner = () => {
  const { t, i18n } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  
  // Cookie consent states
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // Always true and cannot be changed
    functional: false,
    performance: false,
    targeting: false
  });
  
  // State for accordion open/closed states
  const [openAccordions, setOpenAccordions] = useState({
    privacy: true,
    necessary: true,
    functional: true,
    performance: true,
    targeting: true
  });

  // Toggle accordion open/closed state
  const toggleAccordion = (section) => {
    setOpenAccordions({
      ...openAccordions,
      [section]: !openAccordions[section]
    });
  };

  useEffect(() => {
    // Check if cookie consent already exists
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // If no consent exists, show the banner after a slight delay
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // If consent exists, set the saved preferences
      try {
        const savedSettings = JSON.parse(cookieConsent);
        setCookieSettings(savedSettings);
        
        // Initialize Google Tag Manager based on saved preferences
        updateGTMConsent(savedSettings);
        if (savedSettings.targeting) {
          initializeGTM();
        }
      } catch (e) {
        console.error('Error parsing cookie consent', e);
      }
    }
  }, []);

  // Initialize Google Tag Manager
  const initializeGTM = () => {
    if (typeof window === 'undefined' || !window.document) return;
    
    // Check if GTM script already exists
    if (document.getElementById('gtm-script')) return;
    
    // Add Google Tag Manager script
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.innerHTML = `
      // Google Tag Manager
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','G-M52JTFW3SL');
    `;
    document.head.appendChild(script);
  };

  // Update Google Tag Manager with consent settings
  const updateGTMConsent = (settings) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'cookie_consent_update',
        cookie_consent: {
          necessary: settings.necessary,
          functional: settings.functional,
          performance: settings.performance,
          targeting: settings.targeting
        }
      });
      console.log('Cookie consent updated in GTM:', settings);
    }
  };

  const savePreferences = () => {
    // Save preferences to localStorage
    localStorage.setItem('cookieConsent', JSON.stringify(cookieSettings));
    
    // Update GTM with current consent settings
    updateGTMConsent(cookieSettings);
    
    // Initialize GTM if targeting cookies are accepted
    if (cookieSettings.targeting) {
      initializeGTM();
    }
    
    setShowBanner(false);
    setShowPreferences(false);
    
    // Add visual feedback
    showSaveConfirmation();
  };
  
  // Function to show a temporary confirmation message
  const showSaveConfirmation = () => {
    const confirmation = document.createElement('div');
    confirmation.className = 'cookie-save-confirmation';
    confirmation.textContent = t('cookie.saveSuccess');
    document.body.appendChild(confirmation);
    
    // Remove the confirmation after 3 seconds
    setTimeout(() => {
      confirmation.style.opacity = '0';
      setTimeout(() => {
        document.body.removeChild(confirmation);
      }, 500);
    }, 3000);
  };

  const acceptAll = () => {
    // Accept all cookies
    const allAccepted = {
      necessary: true,
      functional: true,
      performance: true,
      targeting: true
    };
    setCookieSettings(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    
    // Update GTM with all consent accepted
    updateGTMConsent(allAccepted);
    
    // Initialize Google Tag Manager
    initializeGTM();
    
    setShowBanner(false);
  };

  const rejectAll = () => {
    // Reject all optional cookies
    const onlyNecessary = {
      necessary: true,
      functional: false,
      performance: false,
      targeting: false
    };
    setCookieSettings(onlyNecessary);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    
    // Update GTM with only necessary consent
    updateGTMConsent(onlyNecessary);
    
    setShowBanner(false);
  };

  const handleToggle = (type) => {
    if (type !== 'necessary') { // Don't allow toggling necessary cookies
      setCookieSettings({
        ...cookieSettings,
        [type]: !cookieSettings[type]
      });
    }
  };

  if (!showBanner && !showPreferences) return null;

  return (
    <>
      {/* Main banner */}
      {showBanner && !showPreferences && (
        <div className="cookie-banner-container">
          <div className="cookie-banner">
            <div className="cookie-banner-content">
              <h2 className="cookie-title">{t('cookie.title')}</h2>
              <p className="cookie-text">
                {t('cookie.description')}
              </p>
              <div className="cookie-actions">
                <button className="cookie-btn cookie-reject-btn" onClick={rejectAll}>{t('cookie.reject')}</button>
                <button className="cookie-btn cookie-settings-btn" onClick={() => setShowPreferences(true)}>{t('cookie.settings')}</button>
                <button className="cookie-btn cookie-accept-btn" onClick={acceptAll}>{t('cookie.accept')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {showPreferences && (
        <div className="cookie-preferences-modal-overlay">
          <div className="cookie-preferences-modal">
            <div className="cookie-modal-header">
              <h2>{t('cookie.preferences')}</h2>
              <button className="cookie-close-btn" onClick={() => setShowPreferences(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="cookie-modal-content">
              <div className="cookie-category">
                <div className="cookie-category-header" onClick={() => toggleAccordion('privacy')}>
                  <h3>{t('cookie.privacyPreferences')}</h3>
                  <span className={`cookie-dropdown-icon ${openAccordions.privacy ? 'open' : 'closed'}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </div>
                {openAccordions.privacy && (
                  <div className="cookie-category-content">
                    <p>
                      {t('cookie.privacyDescription')}
                    </p>
                  </div>
                )}
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header" onClick={() => toggleAccordion('necessary')}>
                  <h3>{t('cookie.necessaryCookies')}</h3>
                  <div className="cookie-toggle cookie-toggle-disabled">
                    <div className="cookie-toggle-switch cookie-toggle-active"></div>
                    <span>{t('cookie.alwaysActive')}</span>
                  </div>
                </div>
                {openAccordions.necessary && (
                  <div className="cookie-category-content">
                    <p>
                      {t('cookie.necessaryDescription')}
                    </p>
                  </div>
                )}
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header" onClick={() => toggleAccordion('functional')}>
                  <h3>{t('cookie.functionalCookies')}</h3>
                  <div className="cookie-toggle" onClick={(e) => { e.stopPropagation(); handleToggle('functional'); }}>
                    <div className={`cookie-toggle-switch ${cookieSettings.functional ? 'cookie-toggle-active' : ''}`}></div>
                  </div>
                </div>
                {openAccordions.functional && (
                  <div className="cookie-category-content">
                    <p>
                      {t('cookie.functionalDescription')}
                    </p>
                  </div>
                )}
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header" onClick={() => toggleAccordion('performance')}>
                  <h3>{t('cookie.performanceCookies')}</h3>
                  <div className="cookie-toggle" onClick={(e) => { e.stopPropagation(); handleToggle('performance'); }}>
                    <div className={`cookie-toggle-switch ${cookieSettings.performance ? 'cookie-toggle-active' : ''}`}></div>
                  </div>
                </div>
                {openAccordions.performance && (
                  <div className="cookie-category-content">
                    <p>
                      {t('cookie.performanceDescription')}
                    </p>
                  </div>
                )}
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header" onClick={() => toggleAccordion('targeting')}>
                  <h3>{t('cookie.targetingCookies')}</h3>
                  <div className="cookie-toggle" onClick={(e) => { e.stopPropagation(); handleToggle('targeting'); }}>
                    <div className={`cookie-toggle-switch ${cookieSettings.targeting ? 'cookie-toggle-active' : ''}`}></div>
                  </div>
                </div>
                {openAccordions.targeting && (
                  <div className="cookie-category-content">
                    <p>
                      {t('cookie.targetingDescription')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="cookie-modal-footer">
              <button className="cookie-btn cookie-save-btn" onClick={savePreferences}>{t('cookie.savePreferences')}</button>
              <button className="cookie-btn cookie-accept-all-btn" onClick={acceptAll}>{t('cookie.allowAll')}</button>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .cookie-banner-container {
          position: fixed;
          bottom: 20px;
          left: 20px;
          width: auto;
          max-width: 460px;
          z-index: 999999;
          padding: 0;
          display: flex;
          justify-content: flex-start;
        }

        .cookie-banner {
          background-color: #fff;
          border-radius: 12px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          width: 100%;
          overflow: hidden;
          animation: slideUp 0.5s ease forwards;
        }

        .cookie-banner-content {
          padding: 15px;
        }

        .cookie-title {
          font-size: 18px;
          font-weight: 600;
          color: #1e3c72;
          margin-bottom: 10px;
        }

        .cookie-text {
          font-size: 14px;
          line-height: 1.5;
          color: #555;
          margin-bottom: 15px;
        }

        .cookie-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .cookie-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .cookie-btn:hover {
          transform: translateY(-2px);
        }

        .cookie-reject-btn {
          background-color: #f8f9fa;
          color: #333;
        }

        .cookie-reject-btn:hover {
          background-color: #e9ecef;
        }

        .cookie-settings-btn {
          background-color: #e9ecef;
          color: #333;
        }

        .cookie-settings-btn:hover {
          background-color: #dee2e6;
        }

        .cookie-accept-btn {
          background-color: #1e3c72;
          color: white;
        }

        .cookie-accept-btn:hover {
          background-color: #2a5298;
        }

        .cookie-preferences-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000000;
          padding: 20px;
        }

        .cookie-preferences-modal {
          background-color: #fff;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          animation: fadeIn 0.3s ease forwards;
          display: flex;
          flex-direction: column;
        }

        .cookie-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 25px 30px;
          border-bottom: 1px solid #eee;
          background-color: #fff;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .cookie-modal-header h2 {
          font-size: 22px;
          color: #1e3c72;
          margin: 0;
        }

        .cookie-close-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #777;
          padding: 5px;
        }

        .cookie-close-btn:hover {
          color: #333;
        }

        .cookie-modal-content {
          padding: 30px;
          overflow-y: auto;
          flex: 1;
        }

        .cookie-category {
          margin-bottom: 30px;
          border: 1px solid #eee;
          border-radius: 10px;
          overflow: hidden;
        }

        .cookie-category-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background-color: #f8f9fa;
          cursor: pointer;
        }

        .cookie-category-header h3 {
          font-size: 18px;
          color: #333;
          margin: 0;
        }

        .cookie-dropdown-icon {
          transition: transform 0.3s ease;
        }

        .cookie-dropdown-icon.open {
          transform: rotate(180deg);
        }

        .cookie-dropdown-icon.closed {
          transform: rotate(0deg);
        }

        .cookie-category-content {
          padding: 20px;
          border-top: 1px solid #eee;
        }

        .cookie-category-content p {
          font-size: 15px;
          line-height: 1.6;
          color: #555;
          margin: 0;
        }

        .cookie-toggle {
          display: flex;
          align-items: center;
          cursor: pointer;
          z-index: 2;
        }

        .cookie-toggle-disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .cookie-toggle-switch {
          width: 50px;
          height: 26px;
          background-color: #ccc;
          border-radius: 13px;
          position: relative;
          margin-right: 10px;
          transition: background-color 0.3s ease;
        }

        .cookie-toggle-switch:before {
          content: '';
          position: absolute;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: white;
          top: 3px;
          left: 3px;
          transition: transform 0.3s ease, background-color 0.3s ease;
        }

        .cookie-toggle-switch.cookie-toggle-active {
          background-color: #1e3c72;
        }

        .cookie-toggle-switch.cookie-toggle-active:before {
          transform: translateX(24px);
        }

        .cookie-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 15px;
          padding: 20px 30px;
          border-top: 1px solid #eee;
          background-color: #fff;
          position: sticky;
          bottom: 0;
          z-index: 10;
        }

        .cookie-save-btn {
          background-color: #1e3c72;
          color: white;
        }

        .cookie-save-btn:hover {
          background-color: #2a5298;
        }

        .cookie-accept-all-btn {
          background-color: #198754;
          color: white;
        }

        .cookie-accept-all-btn:hover {
          background-color: #157347;
        }
        
        .cookie-save-confirmation {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background-color: #198754;
          color: white;
          padding: 15px 25px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000001;
          transition: opacity 0.5s ease;
          font-size: 16px;
        }

        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (max-width: 768px) {
        .cookie-banner-container{
          max-width: 90%;
        left: 4% !important;
        }
          .cookie-banner-content {
            padding: 20px;
          }

          .cookie-title {
            font-size: 20px;
          }

          .cookie-text {
            font-size: 14px;
          }

          .cookie-actions {
            flex-direction: column;
          }

          .cookie-btn {
            width: 100%;
          }

          .cookie-modal-header,
          .cookie-modal-content,
          .cookie-modal-footer {
            padding: 15px;
          }

          .cookie-modal-footer {
            flex-direction: column;
          }
        }
      `}</style>
    </>
  );
};

export default CookieBanner; 